import { useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { api } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartCard } from './chart-card';

function formatMillions(v: number): string {
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    const rounded = m >= 10 ? Math.round(m) : Math.round(m * 10) / 10;
    return `${String(rounded).replace(/\.0$/, '')}M`;
  }
  if (v >= 1_000) return `${Math.round(v / 1_000)}K`;
  return String(v);
}

interface SpendingChartProps {
  department?: string;
}

export function SpendingChart({ department }: SpendingChartProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-monthly', department ?? null],
    queryFn: () => api.getMonthly(6, department),
  });

  return (
    <ChartCard title="Spending — last 6 months" description="Monthly total (VND)">
      {isLoading ? (
        <Skeleton className="h-[240px] w-full" />
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data?.rows ?? []}>
            <defs>
              <linearGradient id="spendingGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="month"
              stroke="hsl(var(--muted-foreground))"
              fontSize={11}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="hsl(var(--muted-foreground))"
              fontSize={11}
              tickFormatter={formatMillions}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip
              contentStyle={{
                background: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 8,
                color: 'hsl(var(--popover-foreground))',
              }}
              formatter={(v) => [
                formatMillions(typeof v === 'number' ? v : parseFloat(String(v ?? 0))) + ' VND',
                'Total',
              ]}
            />
            <Area
              type="monotone"
              dataKey="total_vnd"
              stroke="hsl(var(--chart-1))"
              strokeWidth={2}
              fill="url(#spendingGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}
